import { BLOG_API_BASE } from '../config/site';

export type CommentModel = {
  id: string;
  body: string;
  created: number;
  name: string;
  picture?: string;
  /** True when posted by Leo, rendered with an "Author" badge. */
  isAuthor: boolean;
  /** True when posted by the currently signed-in reader. */
  mine: boolean;
};

/** Error carrying the HTTP status so callers can special-case 401 / 429. */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const res = await fetch(`${BLOG_API_BASE}${path}`, init);

  // 204 carries no body; callers that expect nothing type T as void.
  if (res.status === 204) return undefined as unknown as T;

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    /* a non-JSON body (e.g. a gateway error page) leaves payload null */
  }

  if (!res.ok) {
    throw new ApiError(payload?.error || `Request failed (${res.status})`, res.status);
  }
  return payload as T;
};

const authHeaders = (token?: string): Record<string, string> =>
  token ? { Authorization: `Bearer ${token}` } : {};

/** Ask a question about one blog post. */
export const askTldr = (blogId: string, question: string): Promise<{ answer: string }> =>
  request('/tldr', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blogId, question }),
  });

/**
 * Load a post's comments. The token is optional. It's sent so the API can
 * mark which comments belong to the reader, and omitted for anonymous views.
 */
export const listComments = (blogId: string, token?: string): Promise<{ comments: CommentModel[]; count: number }> =>
  request(`/comments/${encodeURIComponent(blogId)}`, { headers: authHeaders(token) });

export const postComment = (blogId: string, body: string, token: string): Promise<CommentModel> =>
  request(`/comments/${encodeURIComponent(blogId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ body }),
  });

export const deleteComment = (blogId: string, commentId: string, token: string): Promise<void> =>
  request(`/comments/${encodeURIComponent(blogId)}/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

/** Confirm a stored token is still valid on page load. */
export const fetchMe = (token: string): Promise<{ name: string; picture?: string; isAuthor: boolean }> =>
  request('/me', { headers: authHeaders(token) });
